// Developed by Softvative.com
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import CustomFieldsTab from './CustomFieldsTab';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import AddTenantModal from './AddTenantModal';

const {
    FiBell, FiShield, FiGlobe, FiMonitor, FiMail, FiLock, FiSave, FiCheck,
    FiUsers, FiBuilding, FiPlus, FiEdit2, FiTrash2, FiSettings, FiDatabase, FiUser,
    FiFilter, FiChevronDown, FiAlertCircle
} = FiIcons;

const Settings = () => {
    const { user, updateProfile } = useAuth();
    const [activeTab, setActiveTab] = useState('notifications');
    const [saveStatus, setSaveStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);

    // Determine user role
    const isSuperAdmin = user?.roles?.includes('SuperAdmin') || false;
    const isTenantOwner = user?.roles?.includes('TenantOwner') || false;
    const isTenantUser = user?.roles?.includes('TenantUser') || false;

    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Notification Settings
    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        pushNotifications: true,
        incidentAssigned: true,
        incidentResolved: true,
        slaWarnings: true,
        systemUpdates: false,
        weeklyDigest: true
    });

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
        twoFactorAuth: false,
        sessionTimeout: '30',
        passwordExpiry: '90',
        loginAlerts: true
    });

    // Appearance Settings
    const [appearanceSettings, setAppearanceSettings] = useState({
        language: 'en',
        timezone: 'America/New_York',
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h'
    });

    // Tenant Organizations
    const [tenants, setTenants] = useState([]);

    // User Management
    const [users, setUsers] = useState([]);

    // Tenant filter for users
    const [selectedTenant, setSelectedTenant] = useState('All Tenants');
    const [showTenantDropdown, setShowTenantDropdown] = useState(false);

    // Load data based on user role
    useEffect(() => {
        loadData();
        loadRoles();
    }, [user]);

    const loadRoles = async () => {
        try {
            // For now, use hardcoded roles
            // In production, fetch from API: /api/v1/roles
            const roles = [
                { id: 3, name: 'TenantUser' }
            ];

            setAvailableRoles(roles);
        } catch (error) {
            console.error('Error loading roles:', error);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            if (isSuperAdmin) {
                // Super Admin: Load all tenants and all users
                const tenantsResponse = await api.get('/admin/tenants');
                const usersResponse = await api.get('/admin/users');

                if (tenantsResponse.success) {
                    setTenants(tenantsResponse.data.sort((a, b) => a.name.localeCompare(b.name)));
                }
                if (usersResponse.success) {
                    setUsers(usersResponse.data.sort((a, b) => {
                        const aName = a.name || `${a.firstName} ${a.lastName}`;
                        const bName = b.name || `${b.firstName} ${b.lastName}`;
                        return aName.localeCompare(bName);
                    }));
                }
            } else if (isTenantOwner || isTenantUser) {
                // Tenant Owner/User: Load only tenant-specific data
                const usersResponse = await api.get('/admin/get-tenant-users', {
                    headers: { 'X-Tenant-Id': user.tenantId }
                });

                if (usersResponse.success) {
                    setUsers(usersResponse.data.sort((a, b) => {
                        const aName = a.name || `${a.firstName} ${a.lastName}`;
                        const bName = b.name || `${b.firstName} ${b.lastName}`;
                        return aName.localeCompare(bName);
                    }));
                }

                // Set current tenant info
                if (user.tenantId) {
                    setTenants([user.tenantId]);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter users by selected tenant
    const filteredUsers = selectedTenant === 'All Tenants'
        ? users
        : users.filter(u => u.tenant === selectedTenant || u.tenantName === selectedTenant || u.tenant?.name === selectedTenant);

    const handleNotificationChange = (key) => {
        setNotificationSettings({
            ...notificationSettings,
            [key]: !notificationSettings[key]
        });
    };

    const handleSecurityChange = (key, value) => {
        setSecuritySettings({
            ...securitySettings,
            [key]: value
        });
    };

    const handleAppearanceChange = (key, value) => {
        setAppearanceSettings({
            ...appearanceSettings,
            [key]: value
        });
    };

    const handleAddTenant = async () => {
        if (!isSuperAdmin) {
            toast.error('Only Super Admins can add tenants');
            return;
        }

        try {
            const response = await api.post('/admin/tenants', {
                name: 'New Organization',
                slug: 'new-org-' + Date.now()
            });

            if (response.success) {
                toast.success('Tenant created successfully');
                loadData();
            }
        } catch (error) {
            toast.error(error.message || 'Failed to create tenant');
        }
    };

    const handleAddUserClick = () => {
        if (!isSuperAdmin && !isTenantOwner) {
            toast.error('You do not have permission to add users');
            return;
        }
        setIsAddUserModalOpen(true);
    };

    const handleAddUserSubmit = async (formData) => {
        try {
            const endpoint = '/admin/invites';
            const headers = isSuperAdmin ? {} : { 'X-Tenant-Id': user.tenantId };

            const payload = {
                email: formData.email,
                roleId: parseInt(formData.roleId, 10)
            };

            const response = await api.post(endpoint, payload, { headers });

            if (response.success) {
                toast.success('Invitation sent successfully! User will receive an email.');
                loadData();
                return response;
            } else {
                throw new Error(response.message || 'Failed to send invitation');
            }
        } catch (error) {
            console.error('Error sending invite:', error);
            throw error;
        }
    };

    const handleSave = () => {
        setSaveStatus('success');
        toast.success('Settings saved successfully');
        setTimeout(() => setSaveStatus(''), 3000);
    };

    // Define tabs based on user role
    const getTabs = () => {
        const baseTabs = [
            { id: 'notifications', label: 'Notifications', icon: FiBell },
            { id: 'security', label: 'Security', icon: FiShield },
            { id: 'appearance', label: 'Appearance', icon: FiMonitor }
        ];

        if (isSuperAdmin) {
            return [
                ...baseTabs,
                { id: 'tenants', label: 'Tenants', icon: FiBuilding },
                { id: 'users', label: 'All Users', icon: FiUsers },
                { id: 'customFields', label: 'Custom Fields', icon: FiDatabase }
            ];
        } else if (isTenantOwner) {
            return [
                ...baseTabs,
                { id: 'users', label: 'Tenant Users', icon: FiUsers },
                { id: 'customFields', label: 'Custom Fields', icon: FiDatabase }
            ];
        } else {
            // Tenant User - only personal settings
            return baseTabs;
        }
    };

    const tabs = getTabs();

    const handleEditUserClick = (user) => {
        setSelectedUser(user);
        setIsEditUserModalOpen(true);
    };

    const handleUpdateUser = async (userId, data) => {
        await updateProfile(userId, data);
        loadData();
    };

    const [isAddTenantModalOpen, setIsAddTenantModalOpen] = useState(false);

    const handleAddTenantClick = () => {
        if (!isSuperAdmin) {
            toast.error('Only Super Admins can add tenants');
            return;
        }
        setIsAddTenantModalOpen(true);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6 h-full overflow-auto"
        >
            <div className="max-w-6xl mx-auto">
                {/* Header with Role Badge */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
                            <p className="text-gray-600 dark:text-gray-400">Manage your application preferences and system configuration</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {isSuperAdmin && (
                                <span className="px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                                    Super Admin Access
                                </span>
                            )}
                            {isTenantOwner && (
                                <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                                    Tenant Owner
                                </span>
                            )}
                            {isTenantUser && (
                                <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                                    Tenant User
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Role-based info banner */}
                    {isTenantUser && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-start space-x-3">
                                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                                        Limited Settings Access
                                    </h4>
                                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                                        As a Tenant User, you can only modify your personal notification, security, and appearance settings. Contact your Tenant Owner for user management or tenant configuration.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                        <div className="flex space-x-8 px-6 min-w-max">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 whitespace-nowrap ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <SafeIcon icon={tab.icon} className="w-4 h-4" />
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {/* Notifications Tab */}
                        {activeTab === 'notifications' && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notification Preferences</h3>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Receive notifications via email</p>
                                            </div>
                                            <button
                                                onClick={() => handleNotificationChange('emailNotifications')}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.emailNotifications ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            </button>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Push Notifications</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Receive browser push notifications</p>
                                            </div>
                                            <button
                                                onClick={() => handleNotificationChange('pushNotifications')}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.pushNotifications ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            </button>
                                        </div>

                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                            <p className="font-medium text-gray-900 dark:text-white mb-3">Event Notifications</p>
                                            <div className="space-y-3">
                                                {[
                                                    { key: 'incidentAssigned', label: 'Incident Assigned', desc: 'When an incident is assigned to you' },
                                                    { key: 'incidentResolved', label: 'Incident Resolved', desc: 'When an incident is resolved' },
                                                    { key: 'slaWarnings', label: 'SLA Warnings', desc: 'When SLA is about to breach' },
                                                    { key: 'systemUpdates', label: 'System Updates', desc: 'Important system announcements' },
                                                    { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Summary of weekly activities' }
                                                ].map(item => (
                                                    <div key={item.key} className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleNotificationChange(item.key)}
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings[item.key] ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                                }`}
                                                        >
                                                            <span
                                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings[item.key] ? 'translate-x-6' : 'translate-x-1'
                                                                    }`}
                                                            />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security Settings</h3>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Two-Factor Authentication</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Add an extra layer of security</p>
                                            </div>
                                            <button
                                                onClick={() => handleSecurityChange('twoFactorAuth', !securitySettings.twoFactorAuth)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.twoFactorAuth ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            </button>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">Login Alerts</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Get notified of new login attempts</p>
                                            </div>
                                            <button
                                                onClick={() => handleSecurityChange('loginAlerts', !securitySettings.loginAlerts)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.loginAlerts ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    Session Timeout (minutes)
                                                </label>
                                                <select
                                                    value={securitySettings.sessionTimeout}
                                                    onChange={(e) => handleSecurityChange('sessionTimeout', e.target.value)}
                                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                                >
                                                    <option value="15">15 minutes</option>
                                                    <option value="30">30 minutes</option>
                                                    <option value="60">1 hour</option>
                                                    <option value="120">2 hours</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    Password Expiry (days)
                                                </label>
                                                <select
                                                    value={securitySettings.passwordExpiry}
                                                    onChange={(e) => handleSecurityChange('passwordExpiry', e.target.value)}
                                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                                >
                                                    <option value="30">30 days</option>
                                                    <option value="60">60 days</option>
                                                    <option value="90">90 days</option>
                                                    <option value="180">180 days</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Appearance Tab */}
                        {activeTab === 'appearance' && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Appearance Settings</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Language
                                            </label>
                                            <select
                                                value={appearanceSettings.language}
                                                onChange={(e) => handleAppearanceChange('language', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                            >
                                                <option value="en">English</option>
                                                <option value="es">Spanish</option>
                                                <option value="fr">French</option>
                                                <option value="de">German</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Timezone
                                            </label>
                                            <select
                                                value={appearanceSettings.timezone}
                                                onChange={(e) => handleAppearanceChange('timezone', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                            >
                                                <option value="America/New_York">Eastern Time</option>
                                                <option value="America/Chicago">Central Time</option>
                                                <option value="America/Denver">Mountain Time</option>
                                                <option value="America/Los_Angeles">Pacific Time</option>
                                                <option value="Europe/London">London</option>
                                                <option value="Europe/Paris">Paris</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Date Format
                                            </label>
                                            <select
                                                value={appearanceSettings.dateFormat}
                                                onChange={(e) => handleAppearanceChange('dateFormat', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                            >
                                                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Time Format
                                            </label>
                                            <select
                                                value={appearanceSettings.timeFormat}
                                                onChange={(e) => handleAppearanceChange('timeFormat', e.target.value)}
                                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:text-white"
                                            >
                                                <option value="12h">12-hour (AM/PM)</option>
                                                <option value="24h">24-hour</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Tenants Tab - Super Admin Only */}
                        {activeTab === 'tenants' && isSuperAdmin && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-6"
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tenant Organizations</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Manage all tenant organizations across the system</p>
                                    </div>
                                    <button
                                        onClick={handleAddTenantClick}
                                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                    >
                                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                        <span>Add Tenant</span>
                                    </button>
                                </div>

                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Organization</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Slug</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Users</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tenants.map(tenant => (
                                                    <tr key={tenant.id} className="border-b border-gray-100 dark:border-gray-700">
                                                        <td className="py-3 px-4">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                                    <SafeIcon icon={FiBuilding} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                                </div>
                                                                <span className="font-medium text-gray-900 dark:text-white">{tenant.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{tenant.slug || tenant.domain}</td>
                                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{tenant.userCount || tenant.users || 0}</td>
                                                        <td className="py-3 px-4">
                                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                Active
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <div className="flex space-x-2">
                                                                {/* <button className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                                                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                                                </button> */}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Users Tab - Super Admin and Tenant Owner */}
                        {activeTab === 'users' && (isSuperAdmin || isTenantOwner) && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-6"
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {isSuperAdmin ? 'All Users' : 'Tenant Users'}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {isSuperAdmin ? 'Manage users across all tenants' : 'Manage users in your tenant'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleAddUserClick}
                                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                    >
                                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                        <span>Add User</span>
                                    </button>
                                </div>

                                {/* Tenant Filter - Super Admin Only */}
                                {isSuperAdmin && tenants.length > 0 && (
                                    <div className="flex items-center space-x-4">
                                        <div className="relative">
                                            <button
                                                onClick={() => setShowTenantDropdown(!showTenantDropdown)}
                                                className="flex items-center space-x-2 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                            >
                                                <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                                <span className="text-gray-900 dark:text-white">{selectedTenant}</span>
                                                <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                            </button>

                                            {showTenantDropdown && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10"
                                                >
                                                    <div className="py-2">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedTenant('All Tenants');
                                                                setShowTenantDropdown(false);
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                        >
                                                            All Tenants
                                                        </button>
                                                        {tenants.map(tenant => (
                                                            <button
                                                                key={tenant.id}
                                                                onClick={() => {
                                                                    setSelectedTenant(tenant.name);
                                                                    setShowTenantDropdown(false);
                                                                }}
                                                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                            >
                                                                {tenant.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">User</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Email</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Role</th>
                                                    {isSuperAdmin && (
                                                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Tenant</th>
                                                    )}
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                                                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.map(u => (
                                                    <tr key={u.id} className="border-b border-gray-100 dark:border-gray-700">
                                                        <td className="py-3 px-4">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                                                                    <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                                                </div>
                                                                <span className="font-medium text-gray-900 dark:text-white">
                                                                    {u.name || `${u.firstName} ${u.lastName}`}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{u.email}</td>
                                                        <td className="py-3 px-4">
                                                            <span className={`px-2 py-1 text-xs rounded ${u.roles?.[0] === 'SuperAdmin' || u.roles?.[0] === 'TenantOwner'
                                                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                                                u.roles?.[0] === 'TenantUser'
                                                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                                                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                                                }`}>
                                                                {u.role || u.roles?.[0] || u?.userRoles[0]?.role || 'User'}
                                                            </span>
                                                        </td>
                                                        {isSuperAdmin && (
                                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                                                                {u.tenant?.name || u.tenantName || 'N/A'}
                                                            </td>
                                                        )}
                                                        <td className="py-3 px-4">
                                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                {u.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => handleEditUserClick(u)}
                                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                                >
                                                                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Custom Fields Tab - Super Admin and Tenant Owner */}
                        {activeTab === 'customFields' && (isSuperAdmin || isTenantOwner) && (
                            <CustomFieldsTab user={user} />
                        )}

                        {/* Save Button */}
                        <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
                            <div>
                                {saveStatus === 'success' && (
                                    <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                                        <SafeIcon icon={FiCheck} className="w-4 h-4" />
                                        <span className="text-sm">Settings saved successfully</span>
                                    </div>
                                )}
                            </div>
                            {activeTab !== 'customFields' && activeTab !== 'users' && activeTab !== 'tenants' && (
                                <button
                                    onClick={handleSave}
                                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                                    <span>Save Changes</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add User Modal */}
            <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onSuccess={handleAddUserSubmit}
                tenantId={user?.tenantId}
                availableRoles={availableRoles}
            />

            {/* Edit User Modal */}
            <EditUserModal
                isOpen={isEditUserModalOpen}
                onClose={() => setIsEditUserModalOpen(false)}
                user={selectedUser}
                onSuccess={handleUpdateUser}
                availableRoles={availableRoles}
            />

            
            <AddTenantModal
                isOpen={isAddTenantModalOpen}
                onClose={() => setIsAddTenantModalOpen(false)}
                onSuccess={loadData} // refresh tenants list after creation
            />
        </motion.div>
    );
};

export default Settings;